// ============================================================================
// JARVISTRAVEL - FLIGHT ENGINE (client-side, pure)
// Flight-number parsing, a deterministic demo flight builder, the on-time
// history narrative, and the refresh-cadence rule (initial → T-10 → T-5 → T-2
// → T-1 → daily during the trip). This is a FRONT-END stand-in for the real
// flight service (FAA ASWS + BTS history + AeroDataBox per-flight; see
// FLIGHTS-BACKEND.md in PR 2) — the seam swaps to live data without touching
// callers. JT-FL-001.
// ============================================================================

import type { FlightMovement, FlightStatus, FlightOnTimeSummary, TripFlight, TripFlights } from '@/app/types';
import { fromLocalYmd, toLocalYmd } from './dateOnly';

// ── Flight-number parsing ───────────────────────────────────────────────────

export interface ParsedFlightNumber {
  /** IATA marketing carrier code, e.g. "AZ", "B6". */
  carrier: string;
  number: number;
  /** Canonical display form, e.g. "AZ 610". */
  normalized: string;
}

// IATA airline designator: 2 chars, at least one letter (B6, U2, AZ, DL),
// then a 1–4 digit flight number. Case/whitespace-insensitive.
const FLIGHT_NO_RE = /^\s*([A-Z][A-Z0-9]|[0-9][A-Z])\s*(\d{1,4})\s*$/i;

/** Parse "AZ 610" / "dl1234" / " ua 12 " into a normalized flight number. */
export function parseFlightNumber(input: string): ParsedFlightNumber | null {
  const m = (input || '').match(FLIGHT_NO_RE);
  if (!m) return null;
  const carrier = m[1].toUpperCase();
  const number = parseInt(m[2], 10);
  return { carrier, number, normalized: `${carrier} ${number}` };
}

/** Stops are derived, never stored: 1 leg = nonstop. */
export const stopsOf = (f: TripFlight): number => Math.max(0, f.legs.length - 1);

/** The one time to display for a movement: actual > estimated > scheduled.
 *  Falls back to '' so a fully-empty movement can't crash the formatters. */
export const bestTime = (m: FlightMovement): string => m?.actual ?? m?.estimated ?? m?.scheduled ?? '';

/** The two most relevant flights for the Home widget (spec: max two shown):
 *  upcoming first (soonest departure), padded with the most recent past ones
 *  so the card never sits empty mid/post-trip. */
export function nextTwoFlights(bundle: TripFlights, now: Date = new Date()): TripFlight[] {
  const today = toYmd(now);
  const dep = (f: TripFlight) => f.legs[0]?.departure?.scheduled ?? '';
  const byDeparture = bundle.flights.filter((f) => f.legs.length > 0)
    .sort((a, b) => dep(a).localeCompare(dep(b)));
  const upcoming = byDeparture.filter((f) => dep(f).slice(0, 10) >= today);
  const past = byDeparture.filter((f) => dep(f).slice(0, 10) < today);
  return [...upcoming, ...past.reverse()].slice(0, 2);
}

// ── Date helpers (TZ-safe, local) ───────────────────────────────────────────

// Parsing is delegated to fromLocalYmd rather than reimplemented — this used
// to be a second full parser (`iso.split('-')`), which threw on a missing
// value and silently rolled '2026-13-01' into the next year, both defects
// JAR-703 removed from the shared module. Only the anchor differs: flight
// milestones are local-midnight ("check once a day" is a user-calendar
// notion), so the hour stays a parameter here instead of dateOnly's fixed
// noon. Invalid input propagates as an Invalid Date; every caller below
// degrades on NaN rather than acting on a rolled-over date.
const ymdAt = (iso: string, h = 0): Date => {
  const base = fromLocalYmd(iso);
  if (Number.isNaN(base.getTime())) return base;
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), h);
};
// Re-exported rather than reimplemented: two copies of this is how the codebase
// ended up with one correct and one wrong (JAR-610).
export const toYmd = toLocalYmd;
const addDays = (iso: string, n: number): string => {
  const d = ymdAt(iso, 12);
  d.setDate(d.getDate() + n);
  return toYmd(d);
};
const daysUntil = (iso: string, now: Date): number => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((ymdAt(iso).getTime() - today.getTime()) / 86400000);
};

// ── Deterministic pseudo-randomness (stable per trip) ───────────────────────

function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0) / 4294967295; // 0–1
}

// ── Demo route + carrier model ──────────────────────────────────────────────

interface DemoAirport { code: string; city: string; offset: string; airline: string; carrier: string }

const HOME: DemoAirport = { code: 'JFK', city: 'New York', offset: '-04:00', airline: 'Delta Air Lines', carrier: 'DL' };

const AIRPORTS: Record<string, DemoAirport> = {
  rome:       { code: 'FCO', city: 'Rome', offset: '+02:00', airline: 'ITA Airways', carrier: 'AZ' },
  paris:      { code: 'CDG', city: 'Paris', offset: '+02:00', airline: 'Air France', carrier: 'AF' },
  london:     { code: 'LHR', city: 'London', offset: '+01:00', airline: 'British Airways', carrier: 'BA' },
  barcelona:  { code: 'BCN', city: 'Barcelona', offset: '+02:00', airline: 'Iberia', carrier: 'IB' },
  tokyo:      { code: 'HND', city: 'Tokyo', offset: '+09:00', airline: 'ANA', carrier: 'NH' },
  honolulu:   { code: 'HNL', city: 'Honolulu', offset: '-10:00', airline: 'Hawaiian Airlines', carrier: 'HA' },
  dublin:     { code: 'DUB', city: 'Dublin', offset: '+01:00', airline: 'Aer Lingus', carrier: 'EI' },
  sydney:     { code: 'SYD', city: 'Sydney', offset: '+10:00', airline: 'Qantas', carrier: 'QF' },
  'new york': { code: 'JFK', city: 'New York', offset: '-04:00', airline: 'Delta Air Lines', carrier: 'DL' },
};

/** Whole-name or prefix match only (a "York, UK" must not map to New York). */
function findRegistered(city: string): DemoAirport | null {
  for (const key of Object.keys(AIRPORTS)) {
    if (city === key || city.startsWith(`${key} `)) return AIRPORTS[key];
  }
  return null;
}

function airportFor(destination: string): DemoAirport {
  const city = (destination || '').split(',')[0].trim().toLowerCase();
  const registered = findRegistered(city);
  if (registered) return registered;
  const code = city.replace(/[^a-z]/g, '').slice(0, 3).toUpperCase() || 'XXX';
  return { code, city: city ? city.charAt(0).toUpperCase() + city.slice(1) : 'Your destination', offset: '+00:00', airline: 'Delta Air Lines', carrier: 'DL' };
}

const EQUIPMENT = ['Boeing 777-300ER', 'Airbus A330-900', 'Boeing 787-9', 'Airbus A350-900'];

const FORECASTABLE_DAYS = 10; // live-ish status horizon; beyond it: published schedule

/** Status by proximity, with an occasional (deterministic) delay near-term. */
function statusFor(date: string, seed: string, now: Date): { status: FlightStatus; delayMinutes?: number } {
  const until = daysUntil(date, now);
  if (until > FORECASTABLE_DAYS) return { status: 'scheduled' };
  if (until < 0) return { status: 'landed' };
  if (hash(`delay|${seed}`) > 0.85) return { status: 'delayed', delayMinutes: 20 + Math.round(hash(`dmin|${seed}`) * 55) };
  return { status: 'on-time' };
}

/** Add minutes to an airport-local ISO string WITHOUT timezone conversion —
 *  the offset suffix is preserved; only the wall-clock part shifts. */
function addMinutesIso(iso: string, minutes: number): string {
  const date = iso.slice(0, 10);
  const h = Number(iso.slice(11, 13));
  const m = Number(iso.slice(14, 16));
  if (Number.isNaN(h) || Number.isNaN(m)) return iso; // malformed — leave untouched
  const offset = iso.slice(19);
  const total = h * 60 + m + minutes;
  const dayRoll = Math.floor(total / 1440);
  const t = ((total % 1440) + 1440) % 1440;
  const newDate = dayRoll === 0 ? date : addDays(date, dayRoll);
  return `${newDate}T${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}:00${offset}`;
}

/** Build a movement pair carrying the status semantics: a delay sets the
 *  estimated (revised) times + delayMinutes; a landed flight sets actuals. */
function movements(
  dep: Omit<FlightMovement, 'estimated' | 'actual' | 'delayMinutes'>,
  arr: Omit<FlightMovement, 'estimated' | 'actual' | 'delayMinutes'>,
  s: { status: FlightStatus; delayMinutes?: number },
): { departure: FlightMovement; arrival: FlightMovement } {
  if (s.status === 'delayed' && s.delayMinutes) {
    return {
      departure: { ...dep, estimated: addMinutesIso(dep.scheduled, s.delayMinutes), delayMinutes: s.delayMinutes },
      arrival: { ...arr, estimated: addMinutesIso(arr.scheduled, s.delayMinutes), delayMinutes: s.delayMinutes },
    };
  }
  if (s.status === 'landed') {
    return { departure: { ...dep, actual: dep.scheduled }, arrival: { ...arr, actual: arr.scheduled } };
  }
  return { departure: { ...dep }, arrival: { ...arr } };
}

// ── Demo bundle ─────────────────────────────────────────────────────────────

/** Deterministic round-trip flights for demo/offline use, anchored to the
 *  trip's dates. Same trip in → same flights out, so tests and the UI are
 *  stable across renders. */
export function demoTripFlights(
  tripId: string,
  destination: string,
  checkin: string,
  checkout: string,
  now: Date = new Date(),
): TripFlights {
  const dest = airportFor(destination);
  const seed = `${tripId}|${destination}`;
  const num = 100 + Math.round(hash(seed) * 800);
  const equipment = EQUIPMENT[Math.floor(hash(`eq|${seed}`) * EQUIPMENT.length)];
  const depHour = 17 + Math.floor(hash(`dep|${seed}`) * 4); // 17:00–20:00 evening departure

  // Eastbound sits ahead of home, so the outbound is an overnight that leaves the
  // evening BEFORE check-in and lands on check-in morning: the itinerary treats
  // day 1 as the arrival day, and the flight has to agree with it (JAR-1997).
  // Westbound is a same-day daytime hop that departs and arrives on check-in.
  const eastbound = !dest.offset.startsWith('-');
  const depDate = eastbound ? addDays(checkin, -1) : checkin;
  const arriveDate = checkin;
  const outStatus = statusFor(checkin, `out|${seed}`, now);
  const retStatus = statusFor(checkout, `ret|${seed}`, now);

  const outbound: TripFlight = {
    id: `${tripId}-fl-out`,
    kind: 'outbound',
    confirmState: 'confirmed',
    legs: [{
      id: `${tripId}-fl-out-1`,
      flightNumber: `${dest.carrier} ${num}`,
      airline: dest.airline,
      ...movements(
        { code: HOME.code, city: HOME.city, terminal: 'Terminal 1', gate: `B${10 + Math.floor(hash(`g|${seed}`) * 40)}`, scheduled: `${depDate}T${depHour}:30:00${HOME.offset}` },
        { code: dest.code, city: dest.city, terminal: 'Terminal 3', baggageBelt: `${3 + Math.floor(hash(`bb|${seed}`) * 9)}`, scheduled: `${arriveDate}T0${8 + Math.floor(hash(`arr|${seed}`) * 2)}:30:00${dest.offset}` },
        outStatus,
      ),
      equipment,
      status: outStatus.status,
    }],
    onTime: demoOnTime(`${dest.carrier} ${num}`),
  };

  const returnFlight: TripFlight = {
    id: `${tripId}-fl-ret`,
    kind: 'return',
    confirmState: 'confirmed',
    legs: [{
      id: `${tripId}-fl-ret-1`,
      flightNumber: `${dest.carrier} ${num + 1}`,
      airline: dest.airline,
      ...movements(
        { code: dest.code, city: dest.city, terminal: 'Terminal 3', scheduled: `${checkout}T10:45:00${dest.offset}` },
        { code: HOME.code, city: HOME.city, terminal: 'Terminal 1', scheduled: `${checkout}T14:30:00${HOME.offset}` },
        retStatus,
      ),
      equipment,
      status: retStatus.status,
    }],
    onTime: demoOnTime(`${dest.carrier} ${num + 1}`),
  };

  // ASWS-style airport event: a delayed departure implies a ground program at
  // the origin (deterministic — same trip, same alert). US airports only.
  const delayedLeg = [...outbound.legs, ...returnFlight.legs].find((l) => l.status === 'delayed');
  const airportAlerts = delayedLeg
    ? [{ code: delayedLeg.departure.code, message: `Ground delay at ${delayedLeg.departure.code}: departures held ~${delayedLeg.departure.delayMinutes ?? delayedLeg.arrival.delayMinutes ?? 45} min` }]
    : undefined;

  return { tripId, flights: [outbound, returnFlight], updatedAt: now.toISOString(), airportAlerts };
}

// ── Planner intake: extract flight hints from free text ────────────────────
// "Previously booked flights" typed into the Jarvis planner — either exact
// flight numbers ("AZ 610") or route/time phrases ("a flight from Pittsburgh
// to Atlanta to Rome landing at 8:30AM"). The match service turns hints into
// flights; whatever parsing misses, the widget's confirm flow catches.

export type FlightHint =
  | { kind: 'number'; flightNumber: string }
  | { kind: 'route'; cities: string[]; arrivalTime?: string }; // arrivalTime "HH:MM" 24h

const capWords = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());

// Two-letter prose words that must never parse as a lowercase carrier code,
// even right after the word "flight" ("our flight at 830" is a time, not
// AT 830). Typed-uppercase codes ("AT 830") still parse — that's explicit.
const CARRIER_STOPWORDS = new Set([
  'at', 'to', 'in', 'on', 'by', 'of', 'is', 'it', 'my', 'we', 'us', 'or',
  'an', 'as', 'no', 'so', 'up', 'go', 'do', 'if', 'be', 'me', 'am', 'pm',
]);

/** "830AM" / "8:30" / "7pm" (after "landing/arriving … at") → "HH:MM" 24h.
 *  Compact digits split explicitly ("830" → 8:30, "1230" → 12:30) — optional
 *  regex groups won't backtrack for us. */
function parseClockTime(segment: string): string | undefined {
  const m = segment.match(/\b(?:land\w*|arriv\w*)\b[^.;\n]*?\bat\s+(\d{1,4})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return undefined;
  const digits = m[1];
  let h: number;
  let min: number;
  if (m[2]) { h = Number(digits); min = Number(m[2]); }           // "8:30"
  else if (digits.length >= 3) { h = Number(digits.slice(0, -2)); min = Number(digits.slice(-2)); } // "830"
  else { h = Number(digits); min = 0; }                            // "7"
  const meridian = m[3]?.toLowerCase();
  if (meridian === 'pm' && h < 12) h += 12;
  if (meridian === 'am' && h === 12) h = 0;
  if (h > 23 || min > 59) return undefined;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function extractFlightHints(text: string): FlightHint[] {
  const hints: FlightHint[] = [];
  const seen = new Set<string>();
  const src = text || '';

  // 1) Explicit flight numbers. Prose protection: the carrier token must be
  //    UPPERCASE as typed ("AZ 610", "DL1234") or introduced by the word
  //    "flight" ("our flight az 610") — so "landing at 830" never parses.
  const numRe = /\b([A-Za-z][A-Za-z0-9])\s?(\d{1,4})\b/g;
  for (let m = numRe.exec(src); m; m = numRe.exec(src)) {
    const carrierAsTyped = m[1];
    const looksUppercase = carrierAsTyped === carrierAsTyped.toUpperCase() && /[A-Za-z]/.test(carrierAsTyped);
    const flightPrefixed = /\bflight\s*(?:number|no\.?|#)?\s*$/i.test(src.slice(0, m.index));
    if (!looksUppercase && !flightPrefixed) continue;
    // Lowercase prose words never qualify, even flight-prefixed (M4).
    if (!looksUppercase && CARRIER_STOPWORDS.has(carrierAsTyped.toLowerCase())) continue;
    const parsed = parseFlightNumber(`${m[1]} ${m[2]}`);
    if (parsed && !seen.has(parsed.normalized)) {
      seen.add(parsed.normalized);
      hints.push({ kind: 'number', flightNumber: parsed.normalized });
    }
  }

  // 2) Route phrases: "flight/fly(ing) … from A to B (to C) [landing/arriving …]".
  const segRe = /\b(?:flight|fly(?:ing)?)\b([^.;\n]*)/gi;
  for (let m = segRe.exec(src); m; m = segRe.exec(src)) {
    const segment = m[1];
    const fromMatch = segment.match(/\bfrom\s+(.+)$/i);
    if (!fromMatch) continue;
    // The city chain ends where an arrival clause, a date ("on July 28th"),
    // a time ("at 7pm"), or a departure clause starts.
    const chain = fromMatch[1].split(/\s+(?:land\w*|arriv\w*|depart\w*|leav\w*|on\s|at\s)/i)[0];
    const cities = chain
      .split(/\s+to\s+/i)
      .map((c) => capWords(c.trim().replace(/[^A-Za-z' -]/g, '').trim()))
      .filter(Boolean);
    if (cities.length < 2) continue;
    const key = `route|${cities.join('>')}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const arrivalTime = parseClockTime(segment);
    hints.push(arrivalTime ? { kind: 'route', cities, arrivalTime } : { kind: 'route', cities });
  }

  return hints;
}

// Marketing-carrier names for the manual-add path (the live service resolves
// these for real; demo covers the majors + our demo routes).
const CARRIER_NAMES: Record<string, string> = {
  AZ: 'ITA Airways', AF: 'Air France', BA: 'British Airways', IB: 'Iberia',
  NH: 'ANA', HA: 'Hawaiian Airlines', EI: 'Aer Lingus', QF: 'Qantas',
  DL: 'Delta Air Lines', UA: 'United Airlines', AA: 'American Airlines', B6: 'JetBlue',
};

/** Demo stand-in for the manual "add a flight number" service call: resolve a
 *  typed number into a `matched` flight (the user confirms to lock it). Null
 *  when the input isn't a flight number at all. */
export function demoFlightFromNumber(
  tripId: string,
  destination: string,
  input: string,
  date: string,
  now: Date = new Date(),
): TripFlight | null {
  const parsed = parseFlightNumber(input);
  if (!parsed) return null;
  const dest = airportFor(destination);
  const seed = `${tripId}|${parsed.normalized}`;
  const depHour = 8 + Math.floor(hash(`mdep|${seed}`) * 10); // 08:00–17:00
  const s = statusFor(date, `man|${seed}`, now);
  return {
    id: `${tripId}-fl-${parsed.carrier}${parsed.number}`,
    kind: 'other',
    confirmState: 'matched',
    legs: [{
      id: `${tripId}-fl-${parsed.carrier}${parsed.number}-1`,
      flightNumber: parsed.normalized,
      airline: CARRIER_NAMES[parsed.carrier] ?? parsed.carrier,
      ...movements(
        { code: HOME.code, city: HOME.city, scheduled: `${date}T${String(depHour).padStart(2, '0')}:15:00${HOME.offset}` },
        { code: dest.code, city: dest.city, scheduled: `${date}T${String(Math.min(23, depHour + 8)).padStart(2, '0')}:45:00${dest.offset}` },
        s,
      ),
      equipment: EQUIPMENT[Math.floor(hash(`meq|${seed}`) * EQUIPMENT.length)],
      status: s.status,
    }],
    onTime: demoOnTime(parsed.normalized),
  };
}

/** Demo stand-in for the route-hint match: build a (possibly multi-leg) flight
 *  from a city chain. Every city resolving to a registered airport → `matched`;
 *  any unknown city → `unverified` (the widget shows the confirm icon — the
 *  user locks it in from the flight card). Null when the chain is unusable. */
export function demoFlightFromRoute(
  tripId: string,
  cities: string[],
  date: string,
  arrivalTime?: string,
  now: Date = new Date(),
): TripFlight | null {
  if (cities.length < 2) return null;
  const lower = cities.map((c) => c.trim().toLowerCase());
  const registered = lower.every((c) => findRegistered(c) !== null);
  const airports = lower.map((c) => airportFor(c));
  const seed = `${tripId}|route|${lower.join('>')}`;
  const dest = airports[airports.length - 1];
  const baseNum = 200 + Math.round(hash(seed) * 700);
  const equipment = EQUIPMENT[Math.floor(hash(`req|${seed}`) * EQUIPMENT.length)];
  const s = statusFor(date, `route|${seed}`, now);

  const legs = airports.slice(0, -1).map((origin, i) => {
    const destination = airports[i + 1];
    const isLast = i === airports.length - 2;
    const depTime = `${String(6 + i * 4).padStart(2, '0')}:00`;
    const arrTime = isLast && arrivalTime ? arrivalTime : `${String(8 + i * 4).padStart(2, '0')}:30`;
    return {
      id: `${tripId}-fl-route-${i + 1}`,
      flightNumber: `${dest.carrier} ${baseNum + i}`,
      airline: dest.airline,
      ...movements(
        { code: origin.code, city: origin.city, scheduled: `${date}T${depTime}:00${origin.offset}` },
        { code: destination.code, city: destination.city, scheduled: `${date}T${arrTime}:00${destination.offset}` },
        s,
      ),
      equipment,
      status: s.status,
    };
  });

  return {
    id: `${tripId}-fl-route-${lower.join('-').replace(/[^a-z-]/g, '')}`,
    kind: 'other',
    confirmState: registered ? 'matched' : 'unverified',
    legs,
    onTime: demoOnTime(legs[0].flightNumber),
  };
}

function demoOnTime(flightNumber: string): FlightOnTimeSummary {
  const r = hash(`ot|${flightNumber}`);
  return {
    pctOnTime: 62 + Math.round(r * 30),               // 62–92
    medianDelayMin: 5 + Math.round(hash(`md|${flightNumber}`) * 25), // 5–30
    cancelRate: Math.round(hash(`cx|${flightNumber}`) * 3),
    sampleMonths: 6,
    source: 'demo',
  };
}

// ── On-time history narrative ───────────────────────────────────────────────

/** Plain-language read of a flight's on-time history — the "story" behind the
 *  number. Null when there's no history (e.g. international legs outside BTS
 *  coverage with no provider history). */
export function flightOnTimeNarrative(flightNumber: string, s: FlightOnTimeSummary | undefined | null): string | null {
  if (!s) return null;
  const window = `over the last ${s.sampleMonths} months`;
  if (s.pctOnTime < 55 || s.medianDelayMin > 40) {
    return `${flightNumber} often runs late: on time just ${s.pctOnTime}% of the time ${window}, with a median delay of ${s.medianDelayMin} min. Worth padding the plans around it.`;
  }
  const cancel = s.cancelRate >= 3 ? ` Cancellations are rare but happen (${s.cancelRate}%).` : '';
  return `${flightNumber} has arrived on time ${s.pctOnTime}% of the time ${window}, with a median delay of ${s.medianDelayMin} min.${cancel}`;
}

// ── Jarvis planning note ────────────────────────────────────────────────────

// Flight times must read in the AIRPORT's local time. The ISO strings carry
// the airport offset, so we format from the string itself — running them
// through Date would silently convert to the viewer's timezone.
const legLocalHour = (iso: string): number => Number(iso.slice(11, 13));

/** "18:30" of the airport-local clock, as "6:30 PM". */
export const fmtFlightTime = (iso: string): string => {
  const h = legLocalHour(iso);
  const m = iso.slice(14, 16);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${ampm}`;
};

/** Airport-local date as "Jul 28". */
export const fmtFlightDate = (iso: string): string =>
  ymdAt(iso.slice(0, 10), 12).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const fmtLocalTime = fmtFlightTime;

/** What the flights mean for the plan — one concise, planning-relevant line. */
export function flightsInsight(bundle: TripFlights): string | null {
  if (!bundle.flights.length) return null;
  const legs = bundle.flights.flatMap((f) => f.legs);

  const cancelled = legs.find((l) => l.status === 'cancelled');
  if (cancelled) return `${cancelled.flightNumber} is cancelled. Let's rework those days once you're rebooked.`;
  const delayed = legs.find((l) => l.status === 'delayed');
  if (delayed) {
    const mins = delayed.arrival?.delayMinutes ?? delayed.departure?.delayMinutes;
    const by = mins ? ` about ${mins} min` : '';
    return `${delayed.flightNumber} is running${by} late. I'll adjust that day if it slips further.`;
  }

  const outbound = bundle.flights.find((f) => f.kind === 'outbound') ?? bundle.flights[0];
  const lastLeg = outbound.legs[outbound.legs.length - 1];
  const landing = bestTime(lastLeg.arrival);
  if (legLocalHour(landing) < 11) {
    return `You land at ${fmtLocalTime(landing)} local. I've kept that first morning light so you can settle in.`;
  }
  const worst = bundle.flights.map((f) => f.onTime?.pctOnTime ?? 100).sort((a, b) => a - b)[0];
  if (worst < 60) return 'One of your flights runs late more often than not. Worth a loose plan on that day.';
  return 'Flights look steady. I\'ll keep an eye on them as departure gets closer.';
}

// Flight alerts (delays, cancellations, airport events) are no longer derived
// here for the bell — core produces them as real, deduped notification rows
// marked read by UUID (JAR-2388). Airport delays still surface in-context on
// FlightsCard/FlightDetail. A client-derived row carried a non-UUID id that
// core's read handler rejected, so it could never be marked read or cleared.

// ── Refresh cadence ─────────────────────────────────────────────────────────
// The spec: an initial pull when flights are added, then T-10, T-5, T-2, T-1
// (the day before the trip), then once a day during the trip. On-open and
// on-click refreshes call this to decide whether a pull is actually due, so
// tapping the widget ten times doesn't burn ten provider calls.

const MILESTONE_DAYS_OUT = [10, 5, 2, 1];

export interface FlightRefreshCheck {
  due: boolean;
  reason?: 'initial' | 'milestone' | 'daily';
}

export function isFlightRefreshDue(
  lastChecked: string | null,
  checkin: string,
  checkout: string,
  now: Date = new Date(),
): FlightRefreshCheck {
  if (!lastChecked) return { due: true, reason: 'initial' };
  const last = new Date(lastChecked).getTime();
  const nowMs = now.getTime();

  // Pre-trip milestones at local midnight.
  for (const d of MILESTONE_DAYS_OUT) {
    const m = ymdAt(addDays(checkin, -d)).getTime();
    if (last < m && m <= nowMs) return { due: true, reason: 'milestone' };
  }
  // Daily during the trip (checkin..checkout inclusive). Milestones are the
  // USER'S local midnight by design — "check once a day" is a user-calendar
  // notion; airport-TZ precision belongs to the backend scheduler. The loop is
  // bounded so malformed dates can never spin it (366 = a year of trip days).
  let date = checkin;
  for (let i = 0; i < 366 && date <= checkout; i++, date = addDays(date, 1)) {
    const m = ymdAt(date).getTime();
    if (last < m && m <= nowMs) return { due: true, reason: 'daily' };
  }
  return { due: false };
}
